<template>
  <Card :padding="false">
    <template slot="header">Campsites</template>
    <v-btn
      color="red"
      dark
      @click="$router.go(-1)"
    >
      Back
    </v-btn>
    <v-btn
      color="primary"
      dark
      class="mb-2"
      @click="dialog=true"
    >
      Create
    </v-btn>
    <Modal
      v-if="dialog"
      :visible="dialog"
      :ok-visible="true"
      :cancel-visible="true"
      ok-text="Save"
      title="Create"
      @ok="save"
      @cancel="close"
    >
      <v-layout column>
        <!-- identification -->
        <v-text-field
          v-model="editedItem.campsiteNumber"
          label="Number"
        />
        <v-text-field
          v-model="editedItem.campsiteName"
          label="Name"
        />
        <!-- details -->
        <v-text-field
          v-model="editedItem.campsiteDetails"
          label="Details"
        />
        <!-- type -->
        <v-select
          v-model="editedItem.campsiteTypeID"
          :items="campsiteTypes"
          item-text="campsiteTypeWord"
          item-value="campsiteTypeID"
          label="Type"
        />
        <!-- dates -->
        <v-menu
          :nudge-right="30"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <v-text-field
            slot="activator"
            v-model="editedItem.startDate"
            label="Open Date"
            prepend-icon="event"
            readonly
          />
          <v-date-picker v-model="editedItem.startDate"/>
        </v-menu>
        <v-menu
          :nudge-right="30"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <v-text-field
            slot="activator"
            v-model="editedItem.endDate"
            label="Close Date"
            prepend-icon="event"
            readonly
          />
          <v-date-picker v-model="editedItem.endDate"/>
        </v-menu>
        <!-- capacity -->
        <v-text-field
          v-model="editedItem.campsiteCapacity"
          label="Capacity"
        />
        <!-- prices -->
        <v-currency-field
          v-model="editedItem.summerPrice"
          label="Summer Price"
        />
        <v-currency-field
          v-model="editedItem.winterPrice"
          label="Winter Price"
        />
        <v-layout wrap>
          <!-- toggles -->
          <v-switch
            v-model="editedItem.adminOnly"
            true-value="1"
            false-value="0"
            label="Admin"
          />
          <v-switch
            v-model="editedItem.accessible"
            true-value="1"
            false-value="0"
            label="Accessible"
          />
          <v-switch
            v-model="editedItem.campfire"
            true-value="1"
            false-value="0"
            label="Campfire"
          />
          <v-switch
            v-model="editedItem.hiking"
            true-value="1"
            false-value="0"
            label="Hike In"
          />
          <v-switch
            v-model="editedItem.canoe"
            true-value="1"
            false-value="0"
            label="Canoe In"
          />
        </v-layout>
      </v-layout>
    </Modal>
    <v-data-table
      :headers="headers"
      :items="campgroundCampsites"
      hide-actions
    >
      <template
        slot="items"
        slot-scope="props"
      >
        <td>
          {{ (props.item.campsiteNumber + " " + props.item.campsiteName).slice(0, 25) }}
          <Icon
            v-if="props.item.adminOnly==='1'"
            set="fas"
            type="lock"/> <br>
          {{ props.item.campsiteTypeWord }} |
          Capacity: {{ props.item.campsiteCapacity }}
        </td>
        <td>
          {{ props.item.startDate.slice(5) }}<br>
          {{ props.item.endDate.slice(5) }}
        </td>
        <td>
          <p>{{ props.item.campsiteDetails }}</p>
          <Icon
            v-if="props.item.campfire==='1'"
            set="fas"
            type="fire"/>
          <Icon
            v-if="props.item.hiking==='1'"
            set="fas"
            type="walking"/>
          <Icon
            v-if="props.item.canoe==='1'"
            set="fas"
            type="ship"/>
        </td>
        <td>
          Summer: ${{ props.item.summerPrice.slice(0,-3) }}<br>
          Winter: ${{ props.item.winterPrice.slice(0,-3) }}
        </td>
        <td class="text-xs-right">
          <IconButton
            :to="{path: '/campgrounds/blackouts', query: props.item}"
            icon="fa-calendar-times"
            tooltip="Blackouts"
          />
          <IconButton
            icon="fa-edit"
            tooltip="Edit"
            @click="editItem(props.index)"
          />
          <IconButton
            icon="fa-times"
            color="red"
            tooltip="Delete"
            @click="deleteItem(props.index)"
          />
        </td>
      </template>
    </v-data-table>
  </Card>
</template>

<script>
// import vuex mapping
import { mapGetters, mapActions } from 'vuex'

export default {
  props: {
    personGroup: {
      type: String,
      default: '1'
    }
  },
  data () {
    return {
      campgroundID: this.$route.query.campgroundID,
      dialog: false,
      headers: [
        {
          text: 'Campsite',
          sortable: false
        },
        {
          text: 'Open',
          sortable: false
        },
        {
          text: 'Details',
          sortable: false
        },
        {
          text: 'Price',
          sortable: false
        },
        {
          text: '',
          align: 'right',
          sortable: false
        }
      ],
      editedIndex: -1,
      editedItem: {}
    }
  },
  computed: {
    ...mapGetters({
      campsites: 'facilities/campsites',
      campsiteTypes: 'facilities/campsiteTypes'
    }),
    campgroundCampsites () {
      return this.campsites.filter((campsite) =>
        (campsite.campgroundID === this.campgroundID)
      )
    }
  },
  mounted () {
    this.getCampsiteTypes()
    this.getCampsites()
  },
  methods: {
    ...mapActions({
      getCampsiteTypes: 'facilities/getCampsites',
      getCampsites: 'facilities/getCampsiteTypes',
      deleteCampsite: 'facilities/deleteCampsite',
      createCampsite: 'facilities/createCampsite',
      updateCampsite: 'facilities/updateCampsite'
    }),
    editItem (index) {
      this.editedIndex = index
      this.editedItem = this.campgroundCampsites[index]
      this.dialog = true
    },
    deleteItem (index) {
      let confirmation = confirm('Are you sure you want to delete this campsite?')
      if (confirmation) {
        this.deleteCampsite({ campsiteID: this.campgroundCampsites[index].campsiteID })
      }
    },
    close () {
      this.dialog = false
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      }, 300)
    },
    save () {
      if (this.editedIndex === -1) {
        this.editedItem.campgroundID = this.campgroundID
        this.createCampsite(this.editedItem)
      } else {
        this.updateCampsite(this.editedItem)
      }
      console.log('campsite', this.editedItem)
      this.close()
    }
  }
}
</script>
